import { useState } from 'react';
import { HTTPError } from 'ky';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Copy, Eye, Link2, Loader2, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { requestPaxLink, type IPaxLink, type PaxLinkPurpose } from '@/lib/notifications/api';

// The passenger card's «App del pasajero ▾» (2026-11-03: «en la card del pasajero necesitamos
// un botón que nos dé el link de la app del pasajero» — both uses):
//   · «Copiar link para el pasajero» — their own link (48 h), copied and shown, to send another
//     way or read out. Whoever opens it enters as the passenger, and it counts as theirs.
//   · «Ver como el pasajero» — opens their app in a new tab, read-only, recording nothing on
//     this card (no «Abrió el enlace», no «Entró a la app»).
// The link is a bearer credential: kept in this component's state only, never stored or logged.

const ERROR_KEYS: Record<string, string> = {
  pax_link_unconfigured: 'paxLink.error.unconfigured',
  sub_case_not_found: 'paxLink.error.notFound',
  case_not_found: 'paxLink.error.notFound',
  sub_case_without_pnr: 'paxLink.error.notFound',
};

async function errorKeyOf(err: unknown): Promise<string> {
  if (err instanceof HTTPError) {
    if (err.response.status === 403) return 'paxLink.error.forbidden';
    const body = (await err.response.clone().json().catch(() => null)) as { error?: unknown } | null;
    const code = typeof body?.error === 'string' ? body.error : '';
    if (ERROR_KEYS[code]) return ERROR_KEYS[code]!;
  }
  return 'paxLink.error.generic';
}

export function PaxAppLink({
  disruptionUrn,
  caseUrn,
  subCaseUrn,
}: {
  /** The disruption this card is read under — the BFF scopes the PNR to it. */
  disruptionUrn: string | undefined;
  caseUrn: string;
  subCaseUrn: string;
}) {
  const { t, i18n } = useTranslation('pnrActions');
  const [pending, setPending] = useState<PaxLinkPurpose | null>(null);
  const [shared, setShared] = useState<IPaxLink | null>(null);
  const [copied, setCopied] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  /** The preview link when the browser blocked the tab — offered as a link to click instead. */
  const [blocked, setBlocked] = useState<string | null>(null);
  if (!disruptionUrn) return null;

  const copy = async (url: string): Promise<void> => {
    try {
      await navigator.clipboard?.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false); // clipboard denied — the link is on screen to select by hand
    }
  };

  const share = async (): Promise<void> => {
    setErrorKey(null);
    setCopied(false);
    setPending('SHARE');
    try {
      const link = await requestPaxLink(disruptionUrn, subCaseUrn, caseUrn, 'SHARE');
      setShared(link);
      await copy(link.url);
    } catch (err) {
      setErrorKey(await errorKeyOf(err));
    } finally {
      setPending(null);
    }
  };

  const preview = async (): Promise<void> => {
    setErrorKey(null);
    setBlocked(null);
    setPending('PREVIEW');
    // Open the tab inside the click: one opened after the request is a popup browsers block.
    const tab = window.open('about:blank', '_blank');
    try {
      const link = await requestPaxLink(disruptionUrn, subCaseUrn, caseUrn, 'PREVIEW');
      if (tab) {
        tab.opener = null;
        tab.location.href = link.url;
      } else {
        setBlocked(link.url); // popups blocked: the dialog offers the link to click
      }
    } catch (err) {
      tab?.close();
      setErrorKey(await errorKeyOf(err));
    } finally {
      setPending(null);
    }
  };

  const until = shared
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(shared.expiresAt))
    : '';
  const open = !!shared || !!errorKey || !!blocked;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="h-10 shrink-0 gap-1.5 px-3.5 text-sm"
            disabled={pending !== null}
            data-testid="pnr-pax-app"
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Smartphone className="h-4 w-4" aria-hidden />}
            {t('paxLink.trigger')}
            <ChevronDown className="h-4 w-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[15rem]">
          <DropdownMenuItem className="min-h-10 gap-2" onSelect={() => void share()} data-testid="pnr-pax-app-share">
            <Link2 className="h-4 w-4" aria-hidden />
            {t('paxLink.share')}
          </DropdownMenuItem>
          <DropdownMenuItem className="min-h-10 gap-2" onSelect={() => void preview()} data-testid="pnr-pax-app-preview">
            <Eye className="h-4 w-4" aria-hidden />
            {t('paxLink.preview')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (next) return;
          setShared(null);
          setErrorKey(null);
          setBlocked(null);
          setCopied(false);
        }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="pnr-pax-app-dialog">
          <DialogHeader>
            <DialogTitle>{t('paxLink.title')}</DialogTitle>
            <DialogDescription>
              {errorKey ? t(errorKey) : blocked ? t('paxLink.previewBlocked') : t('paxLink.shareWarning')}
            </DialogDescription>
          </DialogHeader>
          {blocked ? (
            <a
              href={blocked}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary underline underline-offset-2"
              onClick={() => setBlocked(null)}
              data-testid="pnr-pax-app-open-preview"
            >
              <Eye className="h-4 w-4" aria-hidden />
              {t('paxLink.openPreview')}
            </a>
          ) : null}
          {shared ? (
            <div className="flex flex-col gap-2">
              <input
                readOnly
                value={shared.url}
                onFocus={(e) => e.currentTarget.select()}
                aria-label={t('paxLink.title')}
                className="h-10 w-full rounded-md border border-input bg-muted/40 px-2 font-mono text-xs"
                data-testid="pnr-pax-app-url"
              />
              <p className="text-xs text-muted-foreground" data-testid="pnr-pax-app-expiry">
                {t('paxLink.expires', { when: until })}
              </p>
            </div>
          ) : null}
          <DialogFooter>
            {shared ? (
              <Button type="button" onClick={() => void copy(shared.url)} data-testid="pnr-pax-app-copy">
                {copied ? <Check className="mr-1 h-4 w-4" aria-hidden /> : <Copy className="mr-1 h-4 w-4" aria-hidden />}
                {copied ? t('paxLink.copied') : t('paxLink.copy')}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
